import {
    BodyText,
    Eyebrow,
    Headline,
    Popover,
    Link as AnvilLink,
    Button,
    Icon,
    Stack,
} from '@servicetitan/design-system';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { KnowledgebaseIcon } from './knowledgebase-icon';
import { observer } from 'mobx-react';
import { useDependencies } from '@servicetitan/react-ioc';
import { EHStore } from '../../stores/eh.store';
import { HELP_CENTER_MODULE_SCOPE } from '../../utils';

export const Help: FC = () => {
    const [helpPopoverOpen, setHelpPopoverOpen] = useState(false);

    const closeHelpPopover = useCallback(() => setHelpPopoverOpen(false), []);
    const toggleOpen = useCallback(() => setHelpPopoverOpen(prev => !prev), []);

    const links = useRef([
        {
            text: 'Manage users in Enterprise Hub',
            link: 'https://help.servicetitan.com/how-to/manage-users-in-enterprise-hub',
        },
        {
            text: 'Manage tenants in Enterprise Hub',
            link: 'https://help.servicetitan.com/how-to/manage-tenants-in-enterprise-hub',
        },
        {
            text: 'TitanExchange in Enterprise Hub',
            link: 'https://help.servicetitan.com/how-to/titanexchange-in-enterprise-hub',
        },
        {
            text: 'Rollup Reporting',
            link: 'https://help.servicetitan.com/how-to/rollup-reporting',
        },
        {
            text: 'Centralized Contact Center',
            link: 'https://help.servicetitan.com/how-to/centralized-contact-center',
        },
    ]);

    return (
        <Popover
            offset={{ distance: 10 }}
            trigger={
                <Button
                    fill={helpPopoverOpen ? 'solid' : 'none'}
                    icon={
                        <Icon color={helpPopoverOpen ? undefined : '#ffffff'} name="help_outline" />
                    }
                    primary={helpPopoverOpen}
                    onClick={toggleOpen}
                />
            }
            headerAlign="space-between"
            onClickOutside={closeHelpPopover}
            open={helpPopoverOpen}
            direction="bl"
            width="m"
            padding="m"
        >
            <Eyebrow className="m-b-2" size="medium">
                Quick help
            </Eyebrow>
            <Stack className="m-b-2" alignItems="center" spacing={1}>
                <Stack.Item>
                    <KnowledgebaseIcon />
                </Stack.Item>
                <Stack.Item className="m-b-half">
                    <AnvilLink
                        target="_blank"
                        href="https://help.servicetitan.com/landing-page/enterprise-hub"
                        className="fw-bold"
                    >
                        Knowledge Base
                    </AnvilLink>
                </Stack.Item>
            </Stack>

            {links.current.map(item => {
                return (
                    <div className="m-b-1" key={item.link}>
                        <AnvilLink target="_blank" href={item.link}>
                            <Stack spacing={2}>
                                <Stack.Item>
                                    <Icon size={18} name="description" />
                                </Stack.Item>
                                <Stack.Item className="m-b-half">{item.text}</Stack.Item>
                            </Stack>
                        </AnvilLink>
                    </div>
                );
            })}

            <div className="m-y-2">
                <Popover.Divider />
            </div>

            <Headline className="m-b-2" size="small">
                Other Resources
            </Headline>

            <div>
                <AnvilLink
                    target="_blank"
                    href="https://help.servicetitan.com/release-notes/latest"
                >
                    <Stack spacing={2}>
                        <Stack.Item>
                            <Icon size={18} name="description" />
                        </Stack.Item>
                        <Stack.Item className="m-b-half">Release Notes</Stack.Item>
                    </Stack>
                </AnvilLink>
            </div>
        </Popover>
    );
};

export const HelpCenterLegacy = observer(() => {
    const [{ ehModules }] = useDependencies(EHStore);
    const containsHelpCenterModule = useMemo(
        () => ehModules.some(m => m.apiScope === HELP_CENTER_MODULE_SCOPE),
        [ehModules],
    );
    if (containsHelpCenterModule) {
        return null;
    }
    return <Help />;
});
